import { type ReactNode, useContext, useMemo, useReducer } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import { CommandPaletteValidationError } from '../command-palette-validation-error';
import { commandPaletteReducer, initialCommandPaletteState } from '../state-machine';

import { CommandPaletteDerivedQueryProvider } from './command-palette-derived-query-provider';
import {
  CommandPaletteActionsContext,
  type CommandPaletteActionsContextValue,
  CommandPaletteStateContext,
} from './command-palette-state-context';
import { useCommandPaletteTelemetry } from './command-palette-telemetry-context';

export interface CommandPaletteStateProviderProps {
  children: ReactNode;
}

/**
 * Provider for command palette UI state.
 *
 * - `useReducer` drives the state machine (`open` with nullable
 *   `rootCommandId`, `view` with `queryAtEntry`, `closed`).
 * - Actions live in {@link CommandPaletteActionsContext}. Their identity
 *   is stable across the churn that matters (typing in particular) and
 *   only changes when `state.mode.type` transitions, which `openView`
 *   reads to decide whether opening a view also opened the palette. Each
 *   action emits its analytics event(s) inline before dispatching to the
 *   reducer. No state mirroring, no mode-diff useEffect.
 * - No actions wrap in `startTransition`. The previous design wrapped
 *   `setRoot` and `openView`, but: (a) we have no Suspense boundaries
 *   in the palette tree for transitions to interact with, (b) every
 *   state change here is cheap (mode flip + listbox swap, or modal
 *   mount), so React's time-slicing never has anything to interrupt,
 *   and (c) snap commit specifically requires the input rewrite + mode
 *   flip in the same paint to avoid showing stale `@flags abc` text.
 *   If a future view's Component is heavy enough to drop frames on
 *   mount, the transition is the right fix -- added at that point
 *   with a measurement to justify it.
 *
 * Action shape note: actions that enter or exit a known entity take
 * the *full* `Command` (not just an id) so they have everything
 * needed for analytics in one place. Callers always have the entity
 * in scope -- the autocomplete computes it from `rootCommandId`,
 * the snap commit gets it from the parser, the view shell has its
 * own command. Passing the full command through means actions don't
 * need to look it up in the registry, which would either bust memo
 * stability or require a ref.
 */
export function CommandPaletteStateProvider({ children }: CommandPaletteStateProviderProps) {
  const [state, dispatch] = useReducer(commandPaletteReducer, initialCommandPaletteState);
  const { onEvent: onEventProp, onError: onErrorProp } = useCommandPaletteTelemetry();
  const onEventRef = useLatestRef(onEventProp);
  const onErrorRef = useLatestRef(onErrorProp);

  // The reducer's `state` is the single source of truth for mode; we
  // never copy it. `openView` reads `state.mode.type` directly to decide
  // whether opening into a view is a fresh palette-open (so the
  // palette-opened/closed pairing stays symmetric). Because of that the
  // memo depends on `state.mode.type` -- it recomputes on real mode
  // transitions (closed/open/view), not on every keystroke
  // (`UPDATE_QUERY` only touches `state.mode.query`).
  //
  // This is safe because `openView` is always dispatched on its own --
  // the bolt button from a closed palette, or a view row from the open
  // list -- never chained after a same-turn `open()`/`close()`. So the
  // committed mode the closure captured is always the mode at call time;
  // there's no stale-within-a-turn window to guard against.
  const actionsValue = useMemo<CommandPaletteActionsContextValue>(
    () => ({
      open: (trigger) => {
        onEventRef.current({ type: 'palette-opened', trigger, timestamp: Date.now() });
        dispatch({ type: 'OPEN_PALETTE' });
      },
      close: () => {
        onEventRef.current({ type: 'palette-closed', timestamp: Date.now() });
        dispatch({ type: 'CLOSE_PALETTE' });
      },
      setQuery: (query) => {
        dispatch({ type: 'UPDATE_QUERY', query });
      },
      openRoot: (id, query) => {
        onEventRef.current({ type: 'root-entered', root: { id }, timestamp: Date.now() });
        dispatch({ type: 'OPEN_ROOT', id, query });
      },
      exitRoot: () => {
        onEventRef.current({ type: 'root-exited', timestamp: Date.now() });
        dispatch({ type: 'EXIT_ROOT' });
      },
      openView: (command, supplied) => {
        let validated: unknown = undefined;
        if (supplied !== undefined && command.prefillSchema !== undefined) {
          const result = command.prefillSchema.safeParse(supplied);
          if (result.success) {
            validated = result.data;
          } else {
            // Low-severity: the view still opens (with undefined
            // prefill), but the dropped payload is a defect worth
            // surfacing in telemetry rather than only the console.
            onErrorRef.current(
              new CommandPaletteValidationError({
                view: { id: command.id },
                message: `openView(${command.id}): prefill failed schema validation, opening with undefined.`,
                cause: result.error,
              }),
            );
          }
        } else if (supplied !== undefined) {
          // No schema declared but caller passed a prefill; the public
          // id-keyed surface already type-checked it against the
          // registry, so pass it through.
          validated = supplied;
        }
        // Opening straight into a view from a closed palette is still
        // an "open" -- emit palette-opened first so the open/close event
        // pairing stays symmetric for session tracking. A view opened
        // while already showing (the open list, or a re-open) doesn't
        // re-fire it.
        if (state.mode.type === 'closed') {
          onEventRef.current({ type: 'palette-opened', trigger: 'programmatic', timestamp: Date.now() });
        }
        onEventRef.current({ type: 'view-entered', view: { id: command.id }, timestamp: Date.now() });
        dispatch({ type: 'OPEN_VIEW', command, prefill: validated });
      },
      exitView: () => {
        onEventRef.current({ type: 'view-exited', timestamp: Date.now() });
        dispatch({ type: 'EXIT_VIEW' });
      },
    }),
    [onErrorRef, onEventRef, state.mode.type],
  );

  return (
    <CommandPaletteStateContext.Provider value={state}>
      <CommandPaletteActionsContext.Provider value={actionsValue}>
        <CommandPaletteDerivedQueryProvider>{children}</CommandPaletteDerivedQueryProvider>
      </CommandPaletteActionsContext.Provider>
    </CommandPaletteStateContext.Provider>
  );
}

/**
 * Internal: read the full state. Re-renders the caller on every
 * reducer dispatch. Reserved for palette internals; consumers use
 * the narrowed public {@link import('../../state').useCommandPaletteState}.
 */
export function useInternalCommandPaletteState() {
  const ctx = useContext(CommandPaletteStateContext);
  if (ctx === null) {
    throw new Error(
      'useInternalCommandPaletteState must be used within a <CommandPaletteStateProvider>. ' +
        'Wrap your tree with <CommandPaletteProvider>.',
    );
  }
  return ctx;
}

/**
 * Internal: read the action dispatchers. Identity is stable across the
 * common churn (notably typing); it changes only when `state.mode.type`
 * transitions, so calling components don't re-render on query updates.
 */
export function useInternalCommandPaletteActions() {
  const ctx = useContext(CommandPaletteActionsContext);
  if (ctx === null) {
    throw new Error(
      'useInternalCommandPaletteActions must be used within a <CommandPaletteStateProvider>. ' +
        'Wrap your tree with <CommandPaletteProvider>.',
    );
  }
  return ctx;
}
